import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';

import Skeleton from '@material-ui/lab/Skeleton';

import pushToGTM, { CLICK_PRODUCT_PAGE_GA4_TAG, CLICK_DATASHEET_GA4_TAG } from '../../utils/analytics/gtm';
import { URL } from '../../utils/constants';
import { DIMENSIONS } from '../../styles/constants';
import { DESIGN_SPEC_ATTRIBUTES } from './constants';
import { bomContainsNullFootprint, bomContainsNullPrice } from './verifications';
import { useTableRowStyles, useTableCellStyles } from '../Card/styles';
import Card from '../Card';
import Link from '../Link';
import { logEventType, middlePartOfRedirectLink } from '../../utils/analytics/adobe/adob';
import queryString from 'query-string';
import { COLORS } from '../../styles/constants.js';

const DesignSpecifications = () => {
	const { t } = useTranslation();
	const tableRowStyles = useTableRowStyles();
	const tableCellClasses = useTableCellStyles();
	const rankingParameters = useSelector(state => state.selectedDesign.detail.rankingParameters);
	const bomItems = useSelector(state => state.selectedDesign.detail.bom);
	const progressActive = useSelector(state => state.tabbar.progressActive);
	const icPartNumber = useSelector(state => state.selectedDesign.icPartNumber);
	const datasheetUrl = useSelector(state => state.selectedDesign.detail.rankingParameters.datasheetUrl);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const domain = window.location.origin;
	const fullURL = window.location.href;
	const path = window.location.pathname;

	const datasheetTrackingParameters = {
		redirectLink: datasheetUrl,
		partNumber: icPartNumber,
		inputParameters: JSON.stringify(inputParametersDL),
		buttonName: 'Open Datasheet',
		wdtTabName: 'summary',
		fullURL: fullURL,
		path: path,
		logEventType: logEventType.DATASHEETLOG
	};

	return (
		<Card
			title={
				progressActive ? (
					<Skeleton height={DIMENSIONS.SKELETON_TITLE_HEIGHT} />
				) : (
					t('components.design.designSpecifications.title')
				)
			}>
			<Table>
				<TableBody>
					{progressActive
						? DESIGN_SPEC_ATTRIBUTES.map(attribute => (
								<Skeleton key={attribute.label} height={DIMENSIONS.SKELETON_TEXT_HEIGHT} />
						  ))
						: DESIGN_SPEC_ATTRIBUTES.filter(
								attribute => rankingParameters[attribute.value] || attribute.value === 'autoQualified'
						  ).map(attribute => (
								<TableRow key={attribute.label} classes={tableRowStyles}>
									<TableCell classes={tableCellClasses}>
										<Typography variant="body2" color="textSecondary">
											{attribute.label}
										</Typography>
									</TableCell>

									{(attribute.value === 'datasheetUrl' || attribute.value === 'partNumber') && (
										<TableCell classes={tableCellClasses}>
											<Tooltip
												title={
													attribute.value === 'partNumber'
														? t('components.general.viewProductsDetails')
														: t('components.general.viewDatasheet')
												}>
												<div classes={tableCellClasses}>
													{attribute.value === 'datasheetUrl' ? (
														<Link
															target="_blank"
															style={{ display: 'flex', alignItems: 'center', color: COLORS.GUMETAL }}
															href={
																domain + middlePartOfRedirectLink + queryString.stringify(datasheetTrackingParameters)
															}
															onClick={() => {
																pushToGTM(CLICK_DATASHEET_GA4_TAG);
															}}>
															<div style={{ display: 'flex', backgroundColor: COLORS.PRIMARY }}>
																<DescriptionOutlinedIcon />
																<Typography variant="body1" style={{ fontWeight: 600, alignContent: 'center' }}>
																	{t('components.design.openDatasheet')}
																</Typography>
															</div>
														</Link>
													) : (
														<Link
															href={URL.PRODUCT_PAGE_PREFIX + rankingParameters.partNumber}
															target="_blank"
															onClick={() => {
																pushToGTM(CLICK_PRODUCT_PAGE_GA4_TAG);
															}}
															style={{ fontSize: 14 }}>
															{rankingParameters.partNumber}
														</Link>
													)}
												</div>
											</Tooltip>
										</TableCell>
									)}
									{attribute.value === 'autoQualified' && (
										<TableCell classes={tableCellClasses}>
											<Typography variant="body1">
												{rankingParameters[attribute.value]
													? t('components.design.designSpecifications.constants.automotive')
													: t('components.design.designSpecifications.constants.industrial')}
											</Typography>
										</TableCell>
									)}
									{attribute.value !== 'datasheetUrl' &&
										attribute.value !== 'partNumber' &&
										attribute.value !== 'autoQualified' && (
											<TableCell classes={tableCellClasses}>
												<Typography variant="body1">
													{(bomContainsNullPrice(bomItems) && attribute.value === 'designPriceFormatted') ||
													(bomContainsNullFootprint(bomItems) && attribute.value === 'designFootprint')
														? 'NA'
														: t(
																`topologies:${attribute.preUnit + rankingParameters[attribute.value] + attribute.unit}`
														  )}
												</Typography>
											</TableCell>
										)}
								</TableRow>
						  ))}
				</TableBody>
			</Table>
		</Card>
	);
};

export default DesignSpecifications;
